<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="tutorials-backup-restore" />

        <div class="markdown-content">
            <h1>GB WhatsApp Backup & Restore Guide – Complete Tutorial</h1>

            <p>Protecting your data is crucial. This comprehensive guide covers everything you need to know about backing up and restoring your GB WhatsApp data, ensuring you never lose important conversations or media.</p>

            <h2>Introduction to Backup and Restore</h2>

            <p>Regular backups are essential for protecting your valuable conversations, media files, and settings. GB WhatsApp offers multiple backup options to ensure your data is always safe and recoverable.</p>

            <h2>Backup Steps</h2>

            <p>Follow these steps to create a complete backup:</p>
            <ol>
                <li><strong>Open Settings:</strong> Launch <a href="/" class="jump-url">GB WhatsApp</a> and go to Settings</li>
                <li><strong>Navigate to Chats:</strong> Tap on "Chats" in the settings menu</li>
                <li><strong>Select Chat Backup:</strong> Tap on "Chat backup" option</li>
                <li><strong>Choose Google Drive:</strong> Select Google Drive as your backup destination</li>
                <li><strong>Set Backup Frequency:</strong> Choose daily, weekly, or monthly backups</li>
                <li><strong>Select Account:</strong> Choose your Google account for backup</li>
                <li><strong>Tap Backup:</strong> Tap the "Backup" button to start backup</li>
            </ol>

            <h2>Cloud Storage Options</h2>

            <p>GB WhatsApp supports various cloud storage solutions:</p>
            <ul>
                <li><strong>Google Drive:</strong> Free cloud storage from Google</li>
                <li><strong>Local Backup:</strong> Create backups stored on your device</li>
                <li><strong>SD Card Backup:</strong> Store backups on external storage</li>
                <li><strong>Computer Backup:</strong> Manual backups to your computer</li>
                <li><strong>Third-Party Cloud:</strong> Some third-party cloud services are supported</li>
            </ul>

            <h2>Data Migration</h2>

            <p>Transfer your data between devices:</p>
            <ul>
                <li><strong>Same Device:</strong> Restore backup on the same device</li>
                <li><strong>New Device:</strong> Transfer data to a new phone</li>
                <li><strong>Cross-Platform:</strong> Move between different Android versions</li>
                <li><strong>Local Transfer:</strong> Use local backup for direct transfer</li>
                <li><strong>Selective Migration:</strong> Choose specific chats to restore</li>
            </ul>

            <h2>File Encryption</h2>

            <p>Protect your backup files with encryption:</p>
            <ul>
                <li><strong>End-to-End Encryption:</strong> Your backups are encrypted</li>
                <li><strong>Password Protection:</strong> Add a password to your backup</li>
                <li><strong>Key Management:</strong> Safely store your encryption key</li>
                <li><strong>Recovery Options:</strong> Recover encrypted backups with key</li>
                <li><strong>Security Best Practices:</strong> Keep your encryption password safe</li>
            </ul>

            <h2>Restore Process</h2>

            <p>Steps to restore your data from a backup:</p>
            <ol>
                <li><strong>Reinstall GB WhatsApp:</strong> Install or reinstall the app</li>
                <li><strong>Verify Number:</strong> Enter your phone number for verification</li>
                <li><strong>Allow Permissions:</strong> Grant all required permissions</li>
                <li><strong>Select Restore:</strong> Choose "Restore" when prompted</li>
                <li><strong>Wait for Completion:</strong> Allow the restore process to complete</li>
                <li><strong>Configure Settings:</strong> Set up your preferences after restore</li>
            </ol>

            <h2>Common Backup Issues</h2>

            <p>Troubleshoot backup problems:</p>
            <ul>
                <li><strong>Google Drive Errors:</strong> Check your Google account connection</li>
                <li><strong>Storage Space:</strong> Ensure sufficient cloud storage available</li>
                <li><strong>Network Issues:</strong> Verify stable internet connection</li>
                <li><strong>Account Problems:</strong> Sign out and sign back into Google</li>
                <li><strong>Version Conflicts:</strong> Ensure compatible GB WhatsApp versions</li>
            </ul>

            <h2>Restore Issues</h2>

            <p>Solve common restoration problems:</p>
            <ul>
                <li><strong>No Backup Found:</strong> Verify backup was completed successfully</li>
                <li><strong>Partial Restore:</strong> Some data may not restore properly</li>
                <li><strong>Media Not Restoring:</strong> Large media files may take longer</li>
                <li><strong>Settings Lost:</strong> Some settings may need manual restoration</li>
                <li><strong>Account Issues:</strong> Verify your phone number matches the backup</li>
            </ul>

            <h2>Backup Best Practices</h2>

            <p>Follow these recommendations for safe backups:</p>
            <ul>
                <li><strong>Regular Backups:</strong> Schedule automatic backups</li>
                <li><strong>Multiple Copies:</strong> Keep local and cloud backups</li>
                <li><strong>Before Updates:</strong> Always backup before app updates</li>
                <li><strong>Verify Backups:</strong> Periodically verify backup integrity</li>
                <li><strong>Document Settings:</strong> Note down important customizations</li>
            </ul>

            <h2>Related Topics</h2>

            <p>Explore more related guides:</p>
            <ul>
                <li><a href="/tutorials/installation/">Installation Guide</a> - Setting up GB WhatsApp</li>
                <li><a href="/features/multi-account/">Multi-Account Features</a> - Managing multiple accounts</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'TutorialsBackupRestore',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}
</style>
